import { FormattedMessage } from 'react-intl';
import type { MessageDescriptor } from 'react-intl';

import { Gap } from 'ui-components/components/flex';
import { FlexColumn } from 'ui-components/components/flex/column';
import { FlexRow } from 'ui-components/components/flex/row';
import { InlineMessage, INLINE_MESSAGE_TYPES } from 'ui-components/components/inline-message';
import { NumberInput } from 'ui-components/components/number-input';
import { Text, TextType } from 'ui-components/components/text';
import { WeightInput } from 'ui-components/components/weight-input';

import { InputLabel } from '../../../../../../../../../address/input-label';
import type { UomFormState } from '../../../../uom-types';
import { DIMENSION_MAX } from '../../uom-modal.constants';
import { messages } from '../../uom-modal.messages';
import style from '../../uom-modal.module.css';

type DimensionsSectionProps = {
  dimensionUnitAbbr?: string;
  errors: Record<string, string | null>;
  form: Pick<UomFormState, 'length' | 'width' | 'height' | 'weight'>;
  weightUnit?: 'ounces' | 'grams';
  onDimensionChange: (field: 'length' | 'width' | 'height', v: number | undefined) => void;
  onWeightChange: (value: number) => void;
};

type DimensionFieldDef = {
  field: 'length' | 'width' | 'height';
  labelMessage: MessageDescriptor;
};

const DIMENSION_FIELDS: DimensionFieldDef[] = [
  { field: 'length', labelMessage: messages.lengthLabel },
  { field: 'width', labelMessage: messages.widthLabel },
  { field: 'height', labelMessage: messages.heightLabel },
];

export const DimensionsSection = ({
  dimensionUnitAbbr,
  errors,
  form,
  weightUnit,
  onDimensionChange,
  onWeightChange,
}: DimensionsSectionProps) => (
  <>
    <div className={style['section-header']}>
      <Text className={style['section-label']} element='span' textType={TextType.Label} value={messages.dimensionsSection} />
      <div className={style['section-rule']} />
    </div>

    <FlexRow className={style['flex-row']} gap={Gap.Space16}>
      {DIMENSION_FIELDS.map(({ field, labelMessage }) => (
        <FlexColumn key={field} className={style['dimension-column']}>
          <InputLabel hasError={!!errors[field]}>
            <FormattedMessage {...labelMessage} values={{ unit: dimensionUnitAbbr ?? 'none' }} />
          </InputLabel>
          <NumberInput
            max={DIMENSION_MAX}
            label={field}
            onChange={(v: number | undefined) => onDimensionChange(field, v)}
            value={form[field] !== '' ? Number(form[field]) : null}
          />
          {errors[field] === 'invalid' && (
            <InlineMessage type={INLINE_MESSAGE_TYPES.error}>
              <FormattedMessage {...messages.dimensionInvalid} />
            </InlineMessage>
          )}
          {errors[field] === 'tooLarge' && (
            <InlineMessage type={INLINE_MESSAGE_TYPES.error}>
              <FormattedMessage {...messages.dimensionTooLarge} />
            </InlineMessage>
          )}
        </FlexColumn>
      ))}
    </FlexRow>

    <FlexRow className={style['flex-row']} gap={Gap.Space16}>
      <FlexColumn className={style['dimension-column']}>
        <InputLabel>
          <FormattedMessage {...messages.weightLabel} />
        </InputLabel>
        <WeightInput
          className={style['weight-input']}
          inputClassName={style['weight-number-input-input']}
          numberInputContainerClassName={style['weight-number-input']}
          unit={weightUnit}
          weight={form.weight !== '' ? Number(form.weight) : null}
          weightInputInnerContainerClassName={style['weight-input-inner']}
          onChange={onWeightChange}
        />
      </FlexColumn>
    </FlexRow>
  </>
);
